import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Badge,
  BadgeProps,
  CircularProgress,
  Tooltip,
  useTheme,
  Avatar,
  colors,
} from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useDTBPackageContext } from '../../context';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams } from '@mui/x-data-grid-pro';
import datetimeHelper from 'helpers/datetimeHelper';
import styled from '@emotion/styled';
import { neutral } from '_lex/_foundations/phxColors';
import EmptyPackagePlaceholder from './EmptyPackagePlaceholder';
import { QuickSearch } from 'components/_atoms';
import { AppContext } from 'views/App/context';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { DTBPackageItem, DTB } from '../../../_types/dtb';
import useManageDTBPackages from '../../_data_source/useManageDTBPackage';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { useQueryClient } from 'react-query';
import { getDifferenceFlagProps } from '../../../utils/flagUtils';
import ActionMenu from 'components/_atoms/ActionMenu';
import LockIcon from '@mui/icons-material/Lock';
import DTBSelectionChips from 'views/App/Portal/DTB/_components/DTBSelectionChips';
import DTBPreview from '../../../Library/Preview';
import { getDTBLock, takeDTBLock } from '../../../Library/_data_sources/manageDTB';
import { getIconAndTooltipForTypeCol } from '../../../Library/List';

export const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: `${neutral[50]}`,
    color: `${neutral[600]}`,
    position: 'static',
    top: 'auto',
    transform: 'none',
  },
}));

const getLockInfo = (lock: any) => {
  if (!lock) {
    return { displayName: '', avatar: '' };
  }
  const displayName = lock.firstName ? `${lock.firstName} ${lock.lastName}` : lock.username;
  const avatar = lock.firstName
    ? [lock.firstName, lock.lastName]
        .filter((x) => x !== undefined)
        .map((x) => x[0].toLocaleUpperCase())
        .join('')
    : lock.username[0].toLocaleUpperCase();
  return { displayName, avatar };
};

const DTBPackageDtbs: React.FC = () => {
  const navigate = useNavigate();
  const { id: packageId } = useParams();
  const { currentOrganizationId, isLexipolStaff, currentProfile } = React.useContext(AppContext);
  const { editedData, setEditedData } = useDTBPackageContext();
  const [isRemoving, setIsRemoving] = useState(false);
  const [search, setSearch] = useState('');
  const { removeDtbs, approveDtbs, update, setDtbsFlags } = useManageDTBPackages();
  const confirm = useConfirm();
  const [selectedDtbs, setSelectedDtbs] = useState<string[]>([]);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isClearingFlags, setIsClearingFlags] = useState(false);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [previewDtbId, setPreviewDtbId] = useState<string | null>(null);
  const [previewDtbName, setPreviewDtbName] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingLockId, setLoadingLockId] = useState<string | null>(null);

  const packageDtbs = editedData?.dtbs || [];

  const filteredDtbs = useMemo(() => {
    return packageDtbs.filter((dtb: DTBPackageItem) => {
      const matchesSearch =
        !search ||
        dtb.name.toLowerCase().includes(search.toLowerCase()) ||
        dtb.manual?.name?.toLowerCase().includes(search.toLowerCase());
      return matchesSearch;
    });
  }, [packageDtbs, search]);

  const handleRemoveDtb = async (dtbId: string) => {
    if (!editedData?.id) return;

    try {
      setIsRemoving(true);
      await removeDtbs(currentOrganizationId, editedData.id, [{ id: dtbId }]);

      setEditedData((prev: any) => ({
        ...prev,
        dtbs: prev.dtbs.filter((dtb: any) => dtb.id !== dtbId),
      }));

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['dtbPackage', currentOrganizationId, editedData.id]);
      queryClient.invalidateQueries(['dtbPackages']);

      enqueueSnackbar(t('dtbs.packages.detail.removeDtbConfirm.success', 'DTB successfully removed.'), {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(
        t('dtbs.packages.detail.removeDtbConfirm.error', 'Failed to remove DTB from Package. Please try again.'),
        { variant: 'error' }
      );
      console.error('Error removing DTB from package:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveDtbWithConfirm = (dtbId: string) => {
    confirm({
      title: t('dtbs.packages.detail.removeDtbConfirm.title', 'Remove DTB from DTB Package'),
      description: t(
        'dtbs.packages.detail.removeDtbConfirm.body',
        'You are about to remove a DTB from this DTB Package. Do you want to proceed?'
      ),
      cancellationText: t('dtbs.packages.detail.removeDtbConfirm.cancel', 'No, keep it'),
      confirmationText: t('dtbs.packages.detail.removeDtbConfirm.confirm', 'Yes, remove'),
      cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      confirmationButtonProps: { variant: 'contained', color: 'error' },
    })
      .then(() => handleRemoveDtb(dtbId))
      .catch(() => {});
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  const handlePreviewClick = (row: any) => {
    setPreviewDtbId(row.id);
    setPreviewDtbName(row.name);
    setPreviewOpen(true);
  };

  const handleEditClick = async (row: any) => {
    try {
      const currentUserId = currentProfile?.user?.id;
      setLoadingLockId(row.id);
      if (row.lock) {
        if (row.lock.userId !== currentUserId) {
          const { displayName } = getLockInfo(row.lock);
          await confirm({
            cancellationButtonProps: {
              variant: 'outlined',
              color: 'neutral',
            },
            confirmationButtonProps: {
              variant: 'contained',
              color: 'error',
            },
            confirmationText: t('dtbs.library.acquireLock.ok'),
            description: t('dtbs.library.acquireLock.body', '', { lockHolder: displayName }),
            title: t('dtbs.library.acquireLock.title'),
          });
          await takeDTBLock(currentOrganizationId, row.id, (lockData) => {
            navigate(`/admin/dtbs/library/${row.id}?packageid=${packageId}`);
          });
        } else {
          navigate(`/admin/dtbs/library/${row.id}?packageid=${packageId}`);
        }
      } else {
        await getDTBLock(currentOrganizationId, row.id, (lockData) => {
          navigate(`/admin/dtbs/library/${row.id}?packageid=${packageId}`);
        });
      }
    } catch (error) {
      console.error('Error handling lock:', error);
    } finally {
      setLoadingLockId(null);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    // Delay clearing the DTB to prevent flash during close animation
    setTimeout(() => {
      setPreviewDtbId(null);
      setPreviewDtbName('');
    }, 300); // Modal close animation duration
  };

  const handleApprove = async () => {
    if (!currentOrganizationId || !selectedDtbs.length) return;
    setIsApproving(true);
    try {
      const userId = currentProfile?.user?.id || '';
      await approveDtbs(
        currentOrganizationId,
        userId,
        selectedDtbs.map((id) => ({ id })),
        () => {
          setEditedData((prev: any) => ({
            ...prev,
            dtbs: prev.dtbs.map((dtb: any) => (selectedDtbs.includes(dtb.id) ? { ...dtb, status: 'APPROVED' } : dtb)),
          }));
          enqueueSnackbar(t('dtbs.packages.detail.approve.success', 'DTB successfully approved.'), {
            variant: 'success',
          });
          setSelectedDtbs([]);
        },
        () => {
          enqueueSnackbar(t('dtbs.packages.detail.approve.error', 'Failed to approve DTB. Please try again.'), {
            variant: 'error',
          });
        }
      );
    } finally {
      setIsApproving(false);
      handleCloseActionMenu();
    }
  };

  const handleClearFlags = async () => {
    if (!currentOrganizationId || !editedData?.id) return;
    setIsClearingFlags(true);
    // Optimistically update the UI for testing: clear the flag in local state for selected DTBs
    const updatedDtbs = packageDtbs.map((dtb: any) =>
      selectedDtbs.includes(dtb.id) ? { ...dtb, differenceFlag: 'NONE', clearanceFlag: 'NONE' } : dtb
    );
    setEditedData((prev: any) => ({ ...prev, dtbs: updatedDtbs }));

    try {
      // Call the backend endpoint to clear the flags
      await setDtbsFlags(
        currentOrganizationId,
        selectedDtbs.map((id) => ({ id, clearanceFlag: 'NONE' }))
      );
      enqueueSnackbar(t('dtbs.packages.detail.actions.clearFlagsSuccess'), {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(t('dtbs.packages.detail.actions.clearFlagsError'), {
        variant: 'error',
      });
    } finally {
      setIsClearingFlags(false);
      setSelectedDtbs([]);
      setSelectedCriteria([]);
    }
  };

  const columns = React.useMemo(
    (): ColumnDef => ({
      name: {
        header: t('dtbs.packages.detail.grid.name'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          // Show a single warning icon only if clearanceFlag is not 'NONE' and differenceFlag is set and not 'NONE'
          const warning = getDifferenceFlagProps(params.row.clearanceFlag);
          const isLocked = !!params.row.lock;
          const { displayName, avatar } = getLockInfo(params.row.lock);
          return (
            <Box sx={{ py: 1, display: 'flex', alignItems: 'center' }}>
              {warning && (
                <Tooltip title={warning.tooltip}>
                  <warning.icon sx={{ color: warning.color, fontSize: 18, mr: 1 }} />
                </Tooltip>
              )}
              <Typography variant='h6'>{params.row.name}</Typography>
              {loadingLockId === params.row.id && (
                <CircularProgress
                  size={16}
                  sx={{ ml: 1 }}
                />
              )}
              {isLocked && (
                <Tooltip title={displayName}>
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: colors.deepPurple[500],
                      color: '#FFF',
                      fontSize: 10,
                      ml: 1,
                    }}
                  >
                    {avatar}
                  </Avatar>
                </Tooltip>
              )}
              {isLocked && (
                <LockIcon
                  color='neutral'
                  fontSize='small'
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          );
        },
      },
      type: {
        header: t('dtbs.library.table.type.title'),
        width: 40,
        renderer: (params: GridCellParams) => {
          const { tooltip, icon: Icon } = getIconAndTooltipForTypeCol(t, params.row.type);
          return (
            <Tooltip title={tooltip}>
              <Icon color='neutral' />
            </Tooltip>
          );
        },
      },
      /* topic: {
        header: t('dtbs.library.table.topic'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.topic}</Typography>,
      }, */
      manualId: {
        header: t('dtbs.packages.detail.grid.manualName'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const { manual } = params.row;

          return <Typography>{manual?.name || '-'}</Typography>;
        },
      },
      status: {
        header: t('dtbs.packages.detail.grid.status'),
        flex: 0.7,
        renderer: (params: GridCellParams) => {
          const status = params.row.status?.toUpperCase() || 'DRAFT';
          const color = status === 'APPROVED' ? 'success' : 'neutral';
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {t(`dtbs.status.${status}`, status)}
            </PhxBadge>
          );
        },
      },
      createdAt: {
        header: t('dtbs.packages.detail.grid.createdAt'),
        flex: 1,
        renderer: (params: GridCellParams) => (
          <Typography>{datetimeHelper.formatDate(params.row.createdAt)}</Typography>
        ),
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.5,
        renderer: (params: GridCellParams) => (
          <ActionMenu
            id={`action_menu_${params.row.id}`}
            items={[
              {
                key: `preview_${params.row.id}`,
                label: t('dtbs.library.detail.preview'),
                onClick: () => handlePreviewClick(params.row),
              },
              {
                key: `edit_${params.row.id}`,
                label: t('dtbs.library.detail.edit'),
                onClick: () => handleEditClick(params.row),
              },
              {
                key: `remove_${params.row.id}`,
                label: isRemoving ? t('dtbs.packages.detail.grid.removing') : t('dtbs.packages.detail.grid.remove'),
                onClick: () => handleRemoveDtbWithConfirm(params.row.id),
                disabled: isRemoving,
              },
            ]}
          />
        ),
      },
    }),
    [t, handleRemoveDtb, isRemoving]
  );

  const renderDtbsContent = () => {
    if (packageDtbs.length === 0) {
      return <EmptyPackagePlaceholder />;
    }
    return (
      <GridSection
        columns={columns}
        data={filteredDtbs}
        query={{ isLoading: isRemoving, isError: false, data: { pages: [{ count: filteredDtbs.length }] } }}
        gridProps={{
          rowHeight: 64,
          columnHeaderHeight: 40,
          autoHeight: false,
          height: '100%',
          checkboxSelection: true,
          isRowSelectable: (row: any) => !row.lock,
          getRowClassName: (params: any) => (params.row.lock ? 'Mui-disabled' : ''),
          onRowSelectionModelChange: (selection: any) => {
            // Only allow selection of rows that are not locked
            const allowedIds = filteredDtbs.filter((dtb: any) => !dtb.lock).map((dtb: any) => dtb.id);
            const filteredSelection = selection.filter((id: string) => allowedIds.includes(id));
            setSelectedDtbs(filteredSelection);
          },
          rowSelectionModel: selectedDtbs,
        }}
      />
    );
  };

  const selectionOptions = [
    { value: 'ALL', label: t('dtbs.packages.detail.actions.selectAll') },
    { value: 'NONE', label: t('dtbs.packages.detail.actions.selectNone') },
    { value: 'DRAFT', label: t('dtbs.packages.detail.actions.selectDraft') },
    { value: 'APPROVED', label: t('dtbs.packages.detail.actions.selectApproved') },
    { value: 'BROKEN_REFERENCE', label: t('dtbs.packages.detail.actions.selectBrokenReference') },
    {
      value: 'DIFFERENT_CONTENT',
      label: t('dtbs.packages.detail.actions.selectDifferentContent'),
    },
    { value: 'PENDING_UPDATE', label: t('dtbs.packages.detail.actions.selectPendingUpdate') },
  ];

  const [selectedCriteria, setSelectedCriteria] = useState<string[]>([]);

  const handleSelectCriterion = (criterion: string) => {
    if (criterion === 'ALL') {
      setSelectedCriteria((prev) => (prev.includes('ALL') ? prev : [...prev, 'ALL']));
      setSelectedDtbs(packageDtbs.filter((dtb: any) => !dtb.lock).map((dtb: any) => dtb.id));
      return;
    }
    if (criterion === 'NONE') {
      setSelectedCriteria(['NONE']);
      setSelectedDtbs([]);
      return;
    }
    // Remove ALL/NONE if present
    setSelectedCriteria((prev) => {
      const filtered = prev.filter((v) => v !== 'ALL' && v !== 'NONE');
      return filtered.includes(criterion) ? filtered : [...filtered, criterion];
    });
    setSelectedDtbs((prev) => {
      let newIds = [...prev];
      if (criterion === 'DRAFT') {
        newIds = Array.from(
          new Set([
            ...newIds,
            ...packageDtbs.filter((dtb: any) => dtb.status === 'DRAFT' && !dtb.lock).map((dtb: any) => dtb.id),
          ])
        );
      } else if (criterion === 'APPROVED') {
        newIds = Array.from(
          new Set([
            ...newIds,
            ...packageDtbs.filter((dtb: any) => dtb.status === 'APPROVED' && !dtb.lock).map((dtb: any) => dtb.id),
          ])
        );
      } else if (criterion === 'BROKEN_REFERENCE') {
        newIds = Array.from(
          new Set([
            ...newIds,
            ...packageDtbs
              .filter((dtb: any) => dtb.differenceFlag === 'CONTENT_MISSING' && !dtb.lock)
              .map((dtb: any) => dtb.id),
          ])
        );
      } else if (criterion === 'DIFFERENT_CONTENT') {
        newIds = Array.from(
          new Set([
            ...newIds,
            ...packageDtbs
              .filter((dtb: any) => dtb.differenceFlag === 'CONTENT_DIFFERENCES' && !dtb.lock)
              .map((dtb: any) => dtb.id),
          ])
        );
      } else if (criterion === 'PENDING_UPDATE') {
        newIds = Array.from(
          new Set([
            ...newIds,
            ...packageDtbs
              .filter((dtb: any) => dtb.differenceFlag === 'PENDING_UPDATES' && !dtb.lock)
              .map((dtb: any) => dtb.id),
          ])
        );
      }
      return newIds;
    });
  };

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, height: '100%', minHeight: 0 }}>
        <Box>
          <PageToolbar
            leftItems={
              <Stack
                direction='row'
                spacing={2}
                sx={{ alignItems: 'center', flex: 1 }}
              >
                <QuickSearch
                  id='keyword_search'
                  initialValue={search}
                  onChange={handleSearchChange}
                  placeholder={t('dtbs.library.filters.search')}
                />
                <ActionMenu
                  id='dtb-select-actions-menu'
                  btnText={t('dtbs.packages.detail.actions.select')}
                  btnProps={{
                    variant: 'outlined',
                    sx: { height: 40 },
                  }}
                  items={selectionOptions.map((option) => ({
                    key: option.value,
                    label: option.label,
                    onClick: () => handleSelectCriterion(option.value),
                  }))}
                />
              </Stack>
            }
            rightItems={
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
              >
                {(isApproving || isClearingFlags) && (
                  <CircularProgress
                    size={24}
                    sx={{ ml: 1 }}
                  />
                )}
                <ActionMenu
                  id='dtb-package-actions-menu'
                  btnText={t('dtbs.packages.detail.actions.package')}
                  btnProps={{
                    variant: 'outlined',
                    endIcon: <MoreVertIcon />,
                    sx: { height: 40 },
                    disabled: selectedDtbs.length === 0 || isApproving || isClearingFlags,
                  }}
                  items={[
                    {
                      key: 'approve',
                      label:
                        selectedDtbs.length > 1
                          ? t('dtbs.packages.detail.actions.bulkApprove', 'Bulk Approve')
                          : t('dtbs.packages.detail.actions.approve'),
                      onClick: handleApprove,
                      disabled: selectedDtbs.length === 0 || isApproving || isClearingFlags,
                    },
                    {
                      key: 'clearFlags',
                      label: t('dtbs.packages.detail.actions.clearFlags', 'Clear Flags'),
                      onClick: handleClearFlags,
                      disabled: selectedDtbs.length === 0 || isApproving || isClearingFlags,
                    },
                  ]}
                />
                <Button
                  variant='contained'
                  onClick={() => navigate(`/admin/dtbs/packages/${packageId}/search`)}
                  sx={{ height: 40 }}
                >
                  {t('dtbs.packages.manageDtbs.addDtbs', 'Add DTBs')}
                </Button>
              </Stack>
            }
          />
          <DTBSelectionChips
            dtbs={packageDtbs}
            selectedDtbs={selectedDtbs}
            selectedCriteria={selectedCriteria}
            setSelectedDtbs={setSelectedDtbs}
            setSelectedCriteria={setSelectedCriteria}
            t={t}
          />
        </Box>
        {renderDtbsContent()}
      </Box>
      <DTBPreview
        dtbId={previewDtbId}
        dtbName={previewDtbName}
        open={previewOpen}
        onClose={handlePreviewClose}
      />
    </>
  );
};

export default DTBPackageDtbs;
